// Minimal API client. Token is read from localStorage on each call so a
// login/logout in another tab immediately propagates.

import { fehlertextAusBody } from "./apiFehlertext";

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "";

function token(): string | null {
  return localStorage.getItem("sb_token");
}

export function setToken(t: string | null) {
  if (t) localStorage.setItem("sb_token", t);
  else localStorage.removeItem("sb_token");
}

export function authToken(): string | null {
  return token();
}

export function apiUrl(path: string): string {
  return `${API_BASE}${path}`;
}

export class ApiError extends Error {
  constructor(public status: number, message: string, public body?: unknown) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const t = token();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(init.headers as Record<string, string> | undefined),
  };
  if (t) headers["Authorization"] = `Bearer ${t}`;

  const r = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (r.status === 204) return undefined as T;
  const text = await r.text();
  let body: unknown = text;
  try {
    body = text ? JSON.parse(text) : undefined;
  } catch {
    /* leave as text */
  }
  if (!r.ok) {
    throw new ApiError(r.status, fehlertextAusBody(body, r.status), body);
  }
  return body as T;
}

export const api = {
  // `init` ist optional und dient bislang genau einem Zweck: einen
  // `AbortController.signal` durchzureichen, damit ein Aufrufer eine
  // Zeitschranke um seinen GET legen kann (`fetch` hat von sich aus KEINE).
  get: <T>(p: string, init?: RequestInit) => request<T>(p, init),
  post: <T>(p: string, body?: unknown) =>
    request<T>(p, { method: "POST", body: body == null ? undefined : JSON.stringify(body) }),
  patch: <T>(p: string, body?: unknown) =>
    request<T>(p, { method: "PATCH", body: JSON.stringify(body ?? {}) }),
  put: <T>(p: string, body?: unknown) =>
    request<T>(p, { method: "PUT", body: JSON.stringify(body ?? {}) }),
  delete: <T = void>(p: string) => request<T>(p, { method: "DELETE" }),
};

// FormData-aware POST (für /documents upload).
export async function postForm<T>(path: string, form: FormData): Promise<T> {
  const t = token();
  const r = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    body: form,
    headers: t ? { Authorization: `Bearer ${t}` } : undefined,
  });
  const text = await r.text();
  let body: unknown = text;
  try {
    body = text ? JSON.parse(text) : undefined;
  } catch {
    /* */
  }
  if (!r.ok) {
    throw new ApiError(r.status, fehlertextAusBody(body, r.status), body);
  }
  return body as T;
}
